/**
 * #763 — what a FREE account may SEE in "What Kin's learned".
 *
 * Until now this hub gated only ADD and EDIT (`useFeature("familyProfileEdit")`).
 * Viewing and deleting were free, so a free account could read — and delete —
 * every fact the Pro setup flow and Pro chat had collected: allergies, schools,
 * sizes, about-you, food rules, providers, focus areas. Kasandra's call on
 * #763 is that free accounts should not see Pro-tier facts at all.
 *
 * ── The carve-out, ENUMERATED rather than assumed ────────────────────────────
 *
 * #763 said to expect "kid names + birthdates" and to confirm it against the
 * code rather than trust the issue. Confirmed, with one correction:
 *
 *   A kid's NAME IS NOT A FACT. It lives in `user_preferences.known_kid_names`
 *   and it is the GROUP TITLE in this hub, never a `family_context` row. So it
 *   is not something this filter can keep or drop — it stays visible for free
 *   accounts because it was never in the filtered set. (`MyKidsSection`, which
 *   owns name and colour editing, is explicitly out of #763's scope.)
 *
 * That leaves ONE key. Every `family_context` writer in the app, and what tier
 * can reach it:
 *
 *   FREE — no gate of any kind:
 *     KidsCrewSetup.tsx:136-146    category "kid", key "birthdate"
 *     MyKidsSection.tsx:435-439    category "kid", key "birthdate"
 *
 *   PRO — behind a gate:
 *     ProSetupFlowModal.tsx        birthdate, partner_name, diaper_size,
 *                                  clothing_size, about_me   (Pro flow)
 *     kin-chat (all chat learning) MEMORY_TOOLS are gated on `memoryEnabled`,
 *                                  and `memoryEnabled = isPro || isAdmin`
 *                                  (kin-chat/index.ts:3158)
 *     TasksTab.tsx:4119            kid sizes — Scheduled Tasks is Pro-gated
 *                                  at the section level
 *     useBabysitterMemory.ts:133   called only from TasksTab, so same gate
 *
 * So: `category === "kid" && key === "birthdate"`.
 *
 * ⚠️ `birthdate` is written by a Pro path TOO (the Pro setup flow). That does
 * not make it a Pro fact. The test is "could a free account have created this
 * herself", and she could — in My Kids, which #763 leaves visible and editable.
 * Hiding a birthday here while the same birthday sits in My Kids one section up
 * would be incoherent rather than stricter.
 *
 * ⚠️ AND THE EXISTING FREE-TIER EMPTY-STATE COPY ALREADY SAYS THIS. The locked
 * card in FamilyProfileSection reads "Right now I keep track of your crew's
 * names and birthdays up in My Kids. Once you're Pro, I'll remember the rest
 * too: allergies, doctors, teachers…". That copy predates #763 and describes
 * exactly this carve-out — the screen just wasn't behaving like it. This is the
 * behaviour catching up to the promise, which is also why #763 needs no new
 * upsell copy invented for it.
 *
 * ── Why a key allowlist and not a group-level split ──────────────────────────
 *
 * One kid's group mixes both tiers in ONE `facts` array — her birthdate beside
 * her allergy and her shoe size. There is no group-level cut that separates
 * them, which is what #763's task 1 warns about. The filter has to be per-fact.
 *
 * ⚠️ ALLOWLIST, NEVER A DENYLIST. A denylist ("hide allergy, school, sizes…")
 * fails OPEN: the next key any writer invents is visible to free accounts by
 * default, silently, and nothing anywhere reports it. An allowlist fails CLOSED
 * — a new key is hidden until somebody deliberately adds it here, next to the
 * enumeration explaining what "free" means. Getting it wrong then costs a
 * hidden fact, not a leaked one.
 */

/** A fact reduced to the two columns the tier decision reads. */
export type TierFact = { category: string; key: string };

/** The one `family_context` category the free carve-out lives in. */
export const FREE_TIER_FACT_CATEGORY = "kid";

/**
 * The keys a free account may see. See the enumeration above before adding to
 * this — an entry here is a statement that a free account could have created
 * the fact herself, not that it looks harmless.
 */
export const FREE_TIER_FACT_KEYS: ReadonlySet<string> = new Set(["birthdate"]);

/** Whether one fact is inside the free carve-out. */
export function isFreeTierFact(fact: TierFact): boolean {
  return fact.category === FREE_TIER_FACT_CATEGORY && FREE_TIER_FACT_KEYS.has(fact.key);
}

/**
 * The facts this account may see.
 *
 * `canSeeProFacts` is passed in rather than read from a hook here so the rule
 * is testable without React and without an entitlement fixture — the gate stays
 * in the component, the rule stays here.
 */
export function visibleFacts<T extends TierFact>(facts: T[], canSeeProFacts: boolean): T[] {
  return canSeeProFacts ? facts : facts.filter(isFreeTierFact);
}
